import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, RotateCcw, Rocket, Bug, Lightbulb, Wrench, Tag, GitCommit, GitPullRequest } from "lucide-react";

const TYPE_ICONS = { bug: Bug, feature: Lightbulb, improvement: Wrench };

const BacklogReviewPopup = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    // Only admins can actually create a release (POST /releases is admin-only).
    // The /dev-backlog/in-progress read endpoint is open to the wider support
    // team (admin/pm_lead/pm/developer), so the previous "rely on 403" gate
    // didn't work — PMs were seeing this popup. Hard-gate on the frontend.
    if (user?.role !== "admin") return;

    const checkInProgress = async () => {
      try {
        const res = await api.get("/dev-backlog/in-progress");
        const data = res.data || [];
        if (data.length === 0) return;

        // De-dupe by id-set hash so the popup re-fires when NEW items get
        // shipped, but doesn't re-show the same set every page load.
        const fingerprint = data.map((x) => x.id).sort().join("|");
        const lastShown = sessionStorage.getItem("backlog_review_fingerprint");
        if (lastShown === fingerprint) return;

        setItems(data);
        const defaults = {};
        data.forEach(item => { defaults[item.id] = "done"; });
        setDecisions(defaults);

        // Prefer the version the deploy job already suggested (auto_ship_version
        // on the freshest pending_review item). Fall back to the bumped next.
        const autoShipped = data.find((x) => x.status === "pending_review" && x.auto_ship_version);
        if (autoShipped?.auto_ship_version) {
          setVersion(autoShipped.auto_ship_version);
        } else {
          try {
            const vRes = await api.get("/releases/next-version");
            setVersion(vRes.data?.next_version || "1.0.0");
          } catch {
            setVersion("1.0.0");
          }
        }

        setOpen(true);
        sessionStorage.setItem("backlog_review_fingerprint", fingerprint);
      } catch {
        // Network or unexpected error — keep silent so we don't spam toasts
        // on every page load.
      }
    };

    const timer = setTimeout(checkInProgress, 2000);
    return () => clearTimeout(timer);
  }, [user?.role]);

  const toggleDecision = (id) => {
    setDecisions(prev => ({
      ...prev,
      [id]: prev[id] === "done" ? "in_progress" : "done",
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const taskIds = Object.entries(decisions)
        .filter(([, status]) => status === "done")
        .map(([id]) => id);
      const reworkIds = Object.entries(decisions)
        .filter(([, status]) => status === "in_progress")
        .map(([id]) => id);

      // Create a release with the done tasks
      await api.post("/releases", {
        version: version.trim() || "1.0.0",
        task_ids: taskIds,
        rework_ids: reworkIds,
        notes: notes.trim(),
      });

      const doneCount = taskIds.length;
      const reworkCount = reworkIds.length;

      if (reworkCount > 0) {
        toast.success(`Release v${version}: ${doneCount} deployed, ${reworkCount} returned for rework`);
      } else {
        toast.success(`Release v${version} created with ${doneCount} tasks!`);
      }
      setOpen(false);
    } catch {
      toast.error("Failed to create release");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || items.length === 0) return null;

  const doneCount = Object.values(decisions).filter(v => v === "done").length;
  const reworkCount = Object.values(decisions).filter(v => v === "in_progress").length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg" data-testid="backlog-review-popup">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Rocket className="w-5 h-5 text-purple-400" />
            Create Release
          </DialogTitle>
          <p className="text-sm text-zinc-500 mt-1">
            Confirm deployed tasks and create a new release version.
          </p>
        </DialogHeader>

        {/* Version input */}
        <div className="flex items-center gap-3 my-2">
          <div className="flex items-center gap-2 flex-1">
            <Tag className="w-4 h-4 text-orange-400 flex-shrink-0" />
            <span className="text-sm text-zinc-400 flex-shrink-0">Version:</span>
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm w-28"
              placeholder="1.0.0"
              data-testid="release-version-input"
            />
          </div>
        </div>

        <div className="space-y-2 my-1 max-h-[280px] overflow-y-auto pr-1">
          {items.map(item => {
            const isDone = decisions[item.id] === "done";
            const TypeIcon = TYPE_ICONS[item.type] || Bug;

            return (
              <div
                key={item.id}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                  isDone
                    ? "border-green-500/40 bg-green-950/20 hover:bg-green-950/30"
                    : "border-orange-500/40 bg-orange-950/20 hover:bg-orange-950/30"
                )}
                onClick={() => toggleDecision(item.id)}
                data-testid={`review-item-${item.id}`}
              >
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors",
                  isDone ? "bg-green-500/20" : "bg-orange-500/20"
                )}>
                  {isDone
                    ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                    : <RotateCcw className="w-4 h-4 text-orange-400" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <TypeIcon className="w-3 h-3 text-zinc-500" />
                    <span className="text-sm font-medium text-zinc-200 truncate">{item.title}</span>
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{item.affected_area}</p>
                  {(item.shipped_commit_sha || item.shipped_pr_url) && (
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500">
                      {item.shipped_commit_sha && (
                        <span className="inline-flex items-center gap-1" title={item.shipped_commit_sha}>
                          <GitCommit className="w-2.5 h-2.5" />
                          <span className="font-mono">{item.shipped_commit_sha.slice(0, 7)}</span>
                        </span>
                      )}
                      {item.shipped_pr_url && (
                        <a
                          href={item.shipped_pr_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300"
                        >
                          <GitPullRequest className="w-2.5 h-2.5" />PR
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-[10px] flex-shrink-0",
                    isDone
                      ? "text-green-400 border-green-500/50 bg-green-500/10"
                      : "text-orange-400 border-orange-500/50 bg-orange-500/10"
                  )}
                >
                  {isDone ? "Deployed" : "Rework"}
                </Badge>
              </div>
            );
          })}
        </div>

        {/* Release notes */}
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm min-h-[60px] resize-none"
          placeholder="Release notes (optional)..."
          data-testid="release-notes-input"
        />

        <div className="text-xs text-zinc-600 text-center">
          Click a task to toggle between Deployed / Rework
        </div>

        <DialogFooter className="gap-2 mt-1">
          <div className="flex-1 text-xs text-zinc-500">
            <span className="text-green-400">{doneCount} deployed</span>
            {reworkCount > 0 && <span className="text-orange-400 ml-2">{reworkCount} rework</span>}
          </div>
          <Button variant="outline" size="sm" className="border-zinc-700" onClick={() => setOpen(false)}>
            Skip
          </Button>
          <Button
            size="sm"
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={handleSubmit}
            disabled={submitting || doneCount === 0}
            data-testid="create-release-btn"
          >
            {submitting ? "Creating..." : `Create Release v${version}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BacklogReviewPopup;
